import { useCallback } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { isStorageResourceNodeType } from '../../explorer/models/azureStorageNodeFactories';
import type { StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import { getStorageFallbackQueryClient, loadStorageServiceFolder, storageContainersQueryKey } from '../services/azureStorageListing';
import { storageSharesQueryKey } from '../../workspace/storage-explorer/services/storageListingKeys';
import {
  ConnectionTreeNode,
  DatabaseExplorerNode,
  FolderNodeBaseMetadata,
  FolderTreeNode,
  createDatabaseObjectNode,
  isDatabaseObjectNode,
  isColumnsFolderNode,
  isColumnNode,
  isPartitionsFolderNode,
  ColumnsFolderNode,
  PartitionsFolderNode,
} from '../../explorer/models/DatabaseTreeModels';
import { findNodeById } from '../../explorer/services/treeService';
import { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import {
  createEmptyMessageNode,
  createErrorNode,
  mapFolderTypeToObjectType,
} from '../services/objectFolderUtils';
import { loadColumnsFolder } from '../services/tableExpansion';
import { loadPartitionsFolder } from '../services/loadPartitionsFolder';
import { useDatabaseStore } from '../store/databaseStore';
import { isSecurityObjectType } from '../services/securityContextMenuService';
import { isSecurityLeafFolderNodeType, loadSecurityFolder } from '../services/securityFolderExpansion';
import { loadAgentSubfolder } from '../services/agentSubfolderExpansion';
import type { AgentJobsSubfolderNode } from '../../explorer/models/nodeTypes';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { canonicalNodeIdForPin } from '../../explorer/models/nodeFactories';
import type { PinnedObjectRef } from '../../../types/electron';
import { intelliSenseRefreshTargetForNode } from '../services/intelliSenseRefreshTarget';
import {
  refreshIntelliSenseForConnection,
  refreshIntelliSenseForDatabase,
} from '../../intellisense/schemaRefresh/refreshTargets';

const AGENT_LEAF_TYPES = new Set(['agent-job', 'agent-operator', 'agent-alert', 'agent-proxy']);

interface NodeRefreshOptions {
  treeData: DatabaseExplorerNode[];
  selectedNodeId?: string;

  fetchObjectsForFolder: (
    objectType: DatabaseObjectType,
    connectionId: string,
    database: string,
  ) => Promise<DatabaseObject[]>;
  invalidateDatabases: (connectionId: string) => void;
  invalidateObjects: (objectType: DatabaseObjectType, connectionId: string, database: string) => void;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  addDatabaseObjects: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  mergePreloadedObjects: (
    connectionId: string,
    database: string,
    objects: DatabaseObject[],
    options?: { complete?: boolean },
  ) => void;
  toggleNodeExpanded: (nodeId: string) => void;
  /** Toggle expand with data loading - use for re-expand after clearing children */
  handleToggleExpand?: (nodeId: string) => void;
  onRefreshRequest?: () => void;
  onReconnect?: (connectionId: string) => Promise<boolean>;
  /**
   * Runs a databases-folder listing for one connection. Owned by
   * `DatabaseExplorer` because a listing is fenced on the connect generation it
   * was STARTED in — capturing that here, one layer away from where connects
   * are observed, is how the fence gets bypassed.
   */
  /**
   * "The connect this refresh was started for is over." Same fence the expand
   * path uses — a held refresh that settles after a reconnect would otherwise
   * write the dead connect's rows (or an error tile) into the successor's tree.
   */
  isConnectStale?: (connectionId: string) => () => boolean;
  /**
   * Re-applies schema grouping to a database node from the metadata currently
   * in `preloadedData`. The preload DEFERS a layout change while the user has
   * the database open, so a database first expanded before its objects landed
   * stays flat for the session — in mode `always` that silently overrides the
   * setting. An explicit Refresh is the retry.
   */
  restructureDatabaseChildren?: (connectionId: string, database: string) => void;
  listDatabasesFolder?: (args: {
    node: FolderTreeNode;
    connectionId: string;
    engine?: DatabaseEngineId;
    forceReload?: boolean;
  }) => Promise<void>;
  /** The app's QueryClient (Azure Storage listings share its cache with the grid and the pin resolver). */
  queryClient?: QueryClient;
}

function getUpdatedPreloadedData(
  connectionId: string,
  database: string,
  objectType: DatabaseObjectType,
  objects: DatabaseObject[],
) {
  const { preloadedData } = useDatabaseStore.getState();
  const existing = preloadedData[connectionId] || {
    databases: [],
    objects: {},
    columns: {},
  };

  const existingDatabaseObjects = existing.objects[database] || [];
  const remainingObjects = existingDatabaseObjects.filter((obj) => obj.type !== objectType);

  return {
    databases: existing.databases,
    columns: existing.columns,
    objects: {
      ...existing.objects,
      [database]: [...remainingObjects, ...objects],
    },
  };
}

export function useNodeRefresh({
  treeData,
  selectedNodeId,

  fetchObjectsForFolder,
  invalidateDatabases,
  invalidateObjects,
  updateNode,
  addDatabaseObjects,
  mergePreloadedObjects,
  isConnectStale,
  restructureDatabaseChildren,
  onRefreshRequest,
  onReconnect,
  listDatabasesFolder,
  queryClient: providedQueryClient,
}: NodeRefreshOptions) {
  const queryClient = providedQueryClient ?? getStorageFallbackQueryClient();
  const refreshObjectFolder = useCallback(
    async (nodeId: string, metadata: FolderNodeBaseMetadata, objectType: DatabaseObjectType) => {
      const { connectionId, database } = metadata;
      if (!connectionId || !database) {
        return;
      }

      // Schema-grouped layout: a schema-container folder sits above this
      // object-type folder, so the folder carries metadata.schema. Mirror
      // loadObjectFolder (the expand path) and only render objects from that
      // schema — the DB-wide fetch / cache still holds every schema's objects.
      const schemaFilter = (metadata as { schema?: string }).schema;
      const applySchemaFilter = (objs: DatabaseObject[]): DatabaseObject[] =>
        schemaFilter ? objs.filter((o) => o.schema === schemaFilter) : objs;

      const isStale = isConnectStale?.(connectionId);
      invalidateObjects(objectType, connectionId, database);
      updateNode(nodeId, { isLoading: true, children: [] });

      try {
        const rawObjects = await fetchObjectsForFolder(objectType, connectionId, database);
        if (isStale?.()) return;
        const objects = applySchemaFilter(rawObjects);

        // Publish the refreshed type's replacement to `preloadedData` FIRST —
        // before any early return, and INCLUDING an empty result. A
        // database-node Refresh regroups from this cache, so a stale entry here
        // restores whatever the refresh just removed (drop the last table, and
        // regrouping rebuilds it). Uses `rawObjects` (DB-wide), never the
        // schema-filtered view, because the cache is not schema-scoped.
        //
        // Refreshing ONE object type replaces that type's rows inside the
        // database's existing entry. Whether the result is still a complete set
        // depends on what was there before: a database that had a complete
        // listing keeps one (only its tables/views/… were re-read), while a
        // database that was never preloaded — or was already partial — only has
        // this one type and must stay marked partial (F06). Marking it partial
        // unconditionally flipped the accessor to `undefined`, making
        // `useEnsureObjectsLoaded` refetch four types in every query tab and
        // silently drop Oracle's packages.
        const before = useDatabaseStore.getState().preloadedData[connectionId];
        const wasComplete =
          before?.objects[database] !== undefined && !before.incomplete?.[database];
        mergePreloadedObjects(
          connectionId,
          database,
          getUpdatedPreloadedData(connectionId, database, objectType, rawObjects).objects[database],
          { complete: wasComplete },
        );

        if (objects.length === 0) {
          updateNode(nodeId, {
            isLoading: false,
            children: [createEmptyMessageNode(connectionId, database, objectType, nodeId)],
          });
          return;
        }

        addDatabaseObjects(connectionId, database, rawObjects);
        updateNode(nodeId, {
          children: objects.map((obj) => createDatabaseObjectNode(obj, nodeId)),
          isLoading: false,
        });
      } catch (error) {
        if (isStale?.()) return;
        const message = error instanceof Error ? error.message : undefined;
        updateNode(nodeId, {
          isLoading: false,
          children: [createErrorNode(connectionId, database, objectType, nodeId, message)],
        });
      }
    },
    [addDatabaseObjects, fetchObjectsForFolder, invalidateObjects, isConnectStale, mergePreloadedObjects, updateNode],
  );

  const refreshColumnsFolder = useCallback(
    async (node: ColumnsFolderNode) => {
      // Clear children and reload with forceReload to bypass the cache check
      updateNode(node.id, { children: [], isLoading: true });
      await loadColumnsFolder({ node, updateNode, forceReload: true });
    },
    [updateNode],
  );

  // `mapFolderTypeToObjectType('partitions-folder')` is undefined (partitions
  // aren't a DatabaseObjectType), so the generic `-folder` branch below
  // silently no-ops for this node type — mirrors refreshColumnsFolder's
  // dedicated branch, needed ahead of that generic check.
  const refreshPartitionsFolder = useCallback(
    async (node: PartitionsFolderNode) => {
      updateNode(node.id, { children: [], isLoading: true });
      await loadPartitionsFolder({ node, updateNode, forceReload: true });
    },
    [updateNode],
  );

  const refreshDatabasesFolder = useCallback(
    async (node: FolderTreeNode, connectionId: string, engine?: DatabaseEngineId) => {
      // Invalidate cache and clear children to force re-fetch
      invalidateDatabases(connectionId);
      updateNode(node.id, { children: [], isLoading: true });

      // `forceReload` bypasses the children check; the explorer's own helper
      // owns the generation fence for the listing it starts.
      await listDatabasesFolder?.({ node, connectionId, engine, forceReload: true });
    },
    [invalidateDatabases, listDatabasesFolder, updateNode],
  );

  const handleRefresh = useCallback(
    (nodeId?: string) => {
      const refreshNode = async (targetNodeId?: string) => {
        if (!targetNodeId) {
          return;
        }

        const node = findNodeById(targetNodeId, treeData);
        if (!node) {
          return;
        }

        // Azure Storage (spec §5.1): a service folder re-lists through the
        // storage listing service; a resource leaf refreshes its parent
        // folder; a pinned storage row invalidates the containers listing so
        // its resolution re-evaluates. All BEFORE the SQL branches — the
        // generic `-folder` branch below must never see a storage folder.
        if (node.type === 'storage-service-folder') {
          const folder = node as StorageServiceFolderNode;
          const staleCheck = isConnectStale?.(folder.metadata.connectionId);
          await loadStorageServiceFolder({
            node: folder,
            queryClient,
            updateNode,
            forceReload: true,
            ...(staleCheck ? { isCurrent: () => !staleCheck() } : {}),
          });
          return;
        }
        if (isStorageResourceNodeType(node.type)) {
          if (node.parentId) await refreshNode(node.parentId);
          return;
        }
        if (node.type === 'pinned-storage') {
          const { connectionId, service } = (node.metadata ?? {}) as { connectionId?: string; service?: string };
          const pinnedKey = connectionId ? (service === 'file' ? storageSharesQueryKey(connectionId) : storageContainersQueryKey(connectionId)) : undefined; // S7
          if (pinnedKey) await queryClient.invalidateQueries({ queryKey: pinnedKey, exact: true });
          return;
        }

        // F9 — Pinned wrappers delegate refresh to the equivalent CANONICAL
        // tree-node id (the one the regular drill-down builds for that
        // database / folder / object). Without this, `pinned-folder` fell
        // through the `'-folder'` branch but `mapFolderTypeToObjectType` for
        // 'pinned-folder' returned undefined; `pinned-database` matched no
        // branch at all — so the Refresh action on any pinned row was a
        // silent no-op.
        //
        // Review finding #4 — the canonical-recursion path silently returned
        // when the canonical node wasn't yet in `treeData` (cold-boot: the
        // user hasn't expanded the parent folder so the leaf hasn't been
        // built). In that case Refresh appeared to do nothing AND the pin's
        // resolution stayed `unresolved` forever. Fall back to invalidating
        // the relevant TanStack Query cache key directly so the resolution
        // re-evaluates even when the canonical isn't materialized.
        if (
          node.type === 'pinned-database' ||
          node.type === 'pinned-schema' ||
          node.type === 'pinned-folder' ||
          node.type === 'pinned-object'
        ) {
          const pinRef = (node as { pinRef?: PinnedObjectRef }).pinRef;
          const connectionId = (node.metadata as FolderNodeBaseMetadata)?.connectionId;
          if (pinRef && connectionId) {
            const canonicalId = canonicalNodeIdForPin(
              connectionId,
              pinRef as Parameters<typeof canonicalNodeIdForPin>[1],
            );
            const canonicalExists = !!findNodeById(canonicalId, treeData);
            if (canonicalExists) {
              refreshNode(canonicalId);
              return;
            }
            // Cold-boot fallback: invalidate the cache key that drives the
            // pin's resolution so the next render re-classifies it. For a
            // pinned-object we invalidate the parent folder's object listing
            // (that's the key resolution probes). For pinned-database /
            // pinned-folder, invalidate the database listing.
            if (pinRef.kind === 'object') {
              const OBJ_TO_TYPE: Record<typeof pinRef.objectType, DatabaseObjectType> = {
                table: 'table',
                view: 'view',
                storedProcedure: 'stored-procedure',
                function: 'function',
              };
              invalidateObjects(OBJ_TO_TYPE[pinRef.objectType], connectionId, pinRef.database);
            } else {
              invalidateDatabases(connectionId);
            }
          }
          return;
        }

        // Handle connection node refresh - attempt reconnect if disconnected
        if (node.type === 'connection') {
          const connNode = node as ConnectionTreeNode;
          if (connNode.metadata?.status === 'disconnected' && onReconnect) {
            // Attempt to reconnect the connection
            await onReconnect(connNode.metadata.id);
            // The reconnect handler will trigger the tree refresh via the status change detection
            return;
          }
          // For connected connections, refresh their databases folder
          const databasesFolder = node.children?.find(
            (child) => child.type === 'databases-folder'
          );
          if (databasesFolder) {
            refreshNode(databasesFolder.id);
          }
          return;
        }

        if (node.type === 'databases-folder') {
          const connectionId = (node.metadata as FolderNodeBaseMetadata)?.connectionId;
          if (connectionId) {
            // Get engine from the parent connection node
            const parentConnectionNode = treeData.find(
              (n) => n.type === 'connection' && (n as ConnectionTreeNode).metadata?.id === connectionId
            ) as ConnectionTreeNode | undefined;
            const engine = parentConnectionNode?.metadata?.engine as DatabaseEngineId | undefined;

            // Directly refresh using loadDatabasesFolder
            void refreshDatabasesFolder(node as FolderTreeNode, connectionId, engine);
          }
        } else if (isColumnsFolderNode(node)) {
          // Handle columns folder refresh
          void refreshColumnsFolder(node);
        } else if (isColumnNode(node)) {
          // For column nodes, refresh the parent columns folder
          const parentNode = findNodeById(node.parentId || '', treeData);
          if (parentNode && isColumnsFolderNode(parentNode)) {
            void refreshColumnsFolder(parentNode);
          }
        } else if (isPartitionsFolderNode(node)) {
          // Must precede the generic `-folder` branch below — see comment
          // on refreshPartitionsFolder's definition.
          void refreshPartitionsFolder(node);
        } else if (isDatabaseObjectNode(node)) {
          // Refreshing an object node (table/view/proc/function) refreshes its parent folder
          if (node.parentId) {
            void refreshNode(node.parentId);
          }
        } else if (isSecurityObjectType(node.type)) {
          // Refreshing a security object refreshes its parent folder
          if (node.parentId) {
            void refreshNode(node.parentId);
          }
        } else if (AGENT_LEAF_TYPES.has(node.type)) {
          // Refreshing an agent leaf node refreshes its parent subfolder
          if (node.parentId) {
            void refreshNode(node.parentId);
          }
        } else if (node.type === 'agent-jobs-subfolder') {
          void loadAgentSubfolder({
            node: node as AgentJobsSubfolderNode,
            updateNode,
            forceReload: true,
          });
        } else if (node.type === 'agent-jobs-folder') {
          // Refresh all expanded agent subfolders (awaited for symmetry with
          // the other container branches — every `refreshNode` resolves when
          // its rows have LANDED, not when they were dispatched).
          await Promise.all(
            (node.children ?? [])
              .filter((child) => child.isExpanded)
              .map((child) => refreshNode(child.id)),
          );
        } else if (isSecurityLeafFolderNodeType(node.type)) {
          void loadSecurityFolder({
            node: node as FolderTreeNode,
            updateNode,
            forceReload: true,
          });
        } else if (
          node.type === 'server-security-folder' ||
          node.type === 'database-security-folder' ||
          node.type === 'pg-roles-folder' ||
          // SQL Server `Programmability -> Types`: a container with no listing
          // of its own, so refreshing it means refreshing its expanded leaves.
          node.type === 'user-types-folder' ||
          // A schema container under the grouped layout is the same shape: it
          // holds the per-schema object folders and lists nothing itself, so
          // without this Refresh on a GROUPED database refetched nothing at all
          // (the generic branch below no-ops when there is no object type).
          node.type === 'schema-container-folder'
        ) {
          // Refresh expanded container subfolders. AWAITED: `refreshNode` is
          // what the database branch below waits on before it restructures, and
          // a dispatch-only call would let the layout flip while these listings
          // were still in flight.
          await Promise.all(
            (node.children ?? [])
              .filter((child) => child.isExpanded)
              .map((child) => refreshNode(child.id)),
          );
        } else if (node.type.includes('-folder') && node.type !== 'databases-folder') {
          const objectType = mapFolderTypeToObjectType(node.type);
          if (objectType) {
            await refreshObjectFolder(node.id, node.metadata as FolderNodeBaseMetadata, objectType);
          }
        } else if (node.type === 'database' && node.children) {
          // Refetch FIRST, restructure LAST. The restructure may flip the
          // layout (that retry is the whole point of a database-node Refresh),
          // which renames every folder id — so a refetch issued before it would
          // write into pre-flip folders that no longer exist, leaving the store
          // fresh and the tree stale until a second Refresh.
          const meta = node.metadata as { connectionId?: string; name?: string };
          // Captured before the await: a reconnect during the folder refetches
          // rebuilds the whole tree, and restructuring afterwards would flip the
          // SUCCESSOR connection's layout with this dead refresh's intent.
          const isStale = meta?.connectionId ? isConnectStale?.(meta.connectionId) : undefined;
          await Promise.all(
            node.children.filter((child) => child.isExpanded).map((child) => refreshNode(child.id)),
          );
          if (isStale?.()) return;
          if (meta?.connectionId && meta.name) {
            restructureDatabaseChildren?.(meta.connectionId, meta.name);
          }
        }
      };

      void refreshNode(nodeId || selectedNodeId);

      // The tree's listings and IntelliSense's schema snapshot are separate
      // caches; a user asking for a Refresh means both. Runs alongside the
      // tree refetch (not awaited): the pill / explorer spinners report it.
      // Only databases IntelliSense has already loaded are re-read — a
      // forced load of an unused database would evict the open tabs'
      // snapshot on the per-connection caches (see `onlyIfLoaded`).
      const target = intelliSenseRefreshTargetForNode(findNodeById(nodeId || selectedNodeId || '', treeData));
      if (target?.kind === 'database') {
        void refreshIntelliSenseForDatabase(target.connectionId, target.database, 'explorer', { onlyIfLoaded: true });
      } else if (target?.kind === 'connection') {
        void refreshIntelliSenseForConnection(target.connectionId, 'explorer');
      }

      if (onRefreshRequest) {
        onRefreshRequest();
      }
    },
    [
      isConnectStale,
      onReconnect,
      onRefreshRequest,
      queryClient,
      restructureDatabaseChildren,
      refreshColumnsFolder,
      refreshDatabasesFolder,
      refreshObjectFolder,
      refreshPartitionsFolder,
      selectedNodeId,
      treeData,
      updateNode,
    ],
  );

  return {
    handleRefresh,
  };
}
